import { mockStore } from './mock-data';

type Dict = Record<string, unknown>;

export type MockRelatorioFiltros = {
  dataInicio?: string;
  dataFim?: string;
};

function parseData(iso: string): Date {
  const d = new Date(iso.includes('T') ? iso : `${iso}T12:00:00`);
  d.setHours(0, 0, 0, 0);
  return d;
}

function dentroPeriodo(iso: string, filtros?: MockRelatorioFiltros): boolean {
  if (!filtros?.dataInicio && !filtros?.dataFim) {
    return true;
  }
  const d = parseData(iso);
  if (filtros.dataInicio && d < parseData(filtros.dataInicio)) {
    return false;
  }
  if (filtros.dataFim && d > parseData(filtros.dataFim)) {
    return false;
  }
  return true;
}

function filtrarComissoesCriacao(filtros?: MockRelatorioFiltros): Dict[] {
  return mockStore.comissoes.filter((c) =>
    dentroPeriodo(String(c['criadoEm'] ?? c['prazoConclusao']), filtros),
  );
}

function filtrarEncerradas(filtros?: MockRelatorioFiltros): Dict[] {
  return mockStore.comissoes.filter(
    (c) =>
      c['status'] === 'ENCERRADA' &&
      dentroPeriodo(String(c['atualizadoEm'] ?? c['prazoConclusao']), filtros),
  );
}

function diasRestantes(prazoConclusao: string): number {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const fim = new Date(`${prazoConclusao}T12:00:00`);
  fim.setHours(0, 0, 0, 0);
  return Math.ceil((fim.getTime() - hoje.getTime()) / 86_400_000);
}

function situacaoCumprimento(c: Dict): 'NO_PRAZO' | 'ATENCAO' | 'ATRASADO' | 'ENCERRADA' {
  if (c['status'] === 'ENCERRADA') {
    return 'ENCERRADA';
  }
  const dias = diasRestantes(String(c['prazoConclusao']));
  if (dias <= 0) {
    return 'ATRASADO';
  }
  if (dias <= 7) {
    return 'ATENCAO';
  }
  return 'NO_PRAZO';
}

export function mockRelatorioPrazos(filtros?: MockRelatorioFiltros) {
  const ativas = filtrarComissoesCriacao(filtros).filter(
    (c) => c['status'] === 'EM_ANDAMENTO' || c['status'] === 'INSTAURADA',
  );
  const itens = [
    { categoria: 'Grupo de Trabalho', situacao: 'NO_PRAZO' as const },
    { categoria: 'Comissão de Estudo', situacao: 'NO_PRAZO' as const },
    { categoria: 'Comissão Especial', situacao: 'NO_PRAZO' as const },
    { categoria: 'Acúmulo Administrativo', situacao: 'ATRASADO' as const, diasAtraso: 3 },
    { categoria: 'Representação Técnica', situacao: 'ATRASADO' as const, diasAtraso: 6 },
  ];
  const atrasadasAtivas = ativas.filter((c) => diasRestantes(String(c['prazoConclusao'])) <= 0).length;
  return {
    totalNoPrazo: Math.max(0, ativas.length - atrasadasAtivas) || itens.filter((i) => i.situacao === 'NO_PRAZO').length,
    totalAtrasadas: atrasadasAtivas || itens.filter((i) => i.situacao === 'ATRASADO').length,
    itens,
  };
}

export function mockRelatoriosPainel(filtros?: MockRelatorioFiltros) {
  const ativas = filtrarComissoesCriacao(filtros).filter(
    (c) => c['status'] === 'EM_ANDAMENTO' || c['status'] === 'INSTAURADA',
  );
  const prazos = mockRelatorioPrazos(filtros);
  return {
    comissoesEmAndamento: ativas.length,
    tempoMedioConclusaoDias: 28,
    conclusoesPorPeriodo: [
      { periodo: 'Mai', conclusoes: 5 },
      { periodo: 'Set', conclusoes: 6 },
      { periodo: 'Nov', conclusoes: 7 },
      { periodo: 'Dez', conclusoes: 4 },
      { periodo: 'Jan', conclusoes: 8 },
      { periodo: 'Fev', conclusoes: 9 },
      { periodo: 'Mar', conclusoes: 6 },
      { periodo: 'Abr', conclusoes: 7 },
    ],
    comissoesPorSecao: [
      { secao: 'PM-1', quantidade: 24 },
      { secao: 'PM-3', quantidade: 21 },
      { secao: 'PM-5', quantidade: 19 },
      { secao: 'PM-4', quantidade: 18 },
      { secao: 'PM-6', quantidade: 12 },
    ],
    prazosPorTipo: prazos.itens,
  };
}

export function mockRelatorioVisaoGeral(filtros?: MockRelatorioFiltros) {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const comissoes = filtrarComissoesCriacao(filtros).map((c) => ({
    id: String(c['id']),
    protocolo: String(c['protocolo']),
    titulo: String(c['titulo']),
    tipo: String(c['tipo']),
    status: String(c['status']),
    secaoProponente: c['secaoProponente'] ? String(c['secaoProponente']) : undefined,
    prazoConclusao: String(c['prazoConclusao']),
    diasRestantes: diasRestantes(String(c['prazoConclusao'])),
  }));
  const ativas = comissoes.filter((c) => c.status === 'EM_ANDAMENTO' || c.status === 'INSTAURADA');
  const encerradas = comissoes.filter((c) => c.status === 'ENCERRADA');
  const atrasadas = ativas.filter((c) => c.diasRestantes <= 0);
  return {
    totalComissoes: comissoes.length,
    ativas: ativas.length,
    encerradas: encerradas.length,
    atrasadas: atrasadas.length,
    comissoes,
  };
}

export function mockRelatorioEncerradasTrimestre(filtros?: MockRelatorioFiltros) {
  const encerradas = filtrarEncerradas(filtros).map((c) => ({
      id: String(c['id']),
      protocolo: String(c['protocolo']),
      titulo: String(c['titulo']),
      dataEncerramento: String(c['atualizadoEm'] ?? c['prazoConclusao']),
      demandaVinculadaId: c['demandaVinculadaId'] ? String(c['demandaVinculadaId']) : undefined,
    }));
  return {
    trimestre: '1º trimestre / 2026',
    totalEncerradas: encerradas.length || 3,
    comDemandaMg3p: encerradas.filter((c) => c.demandaVinculadaId).length,
    comissoes:
      encerradas.length > 0
        ? encerradas
        : [
            {
              id: 'mock-enc-1',
              protocolo: 'COM-EM-2025-042',
              titulo: 'Análise de comunicação interna',
              dataEncerramento: '2026-05-01T08:00:00Z',
            },
            {
              id: 'mock-enc-2',
              protocolo: 'COM-EM-2025-038',
              titulo: 'Revisão de normas internas',
              dataEncerramento: '2026-03-15T10:00:00Z',
              demandaVinculadaId: 'dem-mock-001',
            },
            {
              id: 'mock-enc-3',
              protocolo: 'COM-EM-2025-031',
              titulo: 'Estudo logístico PM-4',
              dataEncerramento: '2026-02-20T14:00:00Z',
            },
          ],
  };
}

export function mockRelatorioDesempenhoAnual(filtros?: MockRelatorioFiltros) {
  const exercicio = filtros?.dataInicio
    ? new Date(filtros.dataInicio).getFullYear()
    : new Date().getFullYear();
  const instauradas = filtrarComissoesCriacao({
    dataInicio: `${exercicio}-01-01`,
    dataFim: `${exercicio}-12-31`,
    ...filtros,
  });
  const encerradas = filtrarEncerradas({
    dataInicio: filtros?.dataInicio ?? `${exercicio}-01-01`,
    dataFim: filtros?.dataFim ?? `${exercicio}-12-31`,
  });
  return {
    exercicio,
    totalInstauradas: instauradas.length || 94,
    totalEncerradas: encerradas.length || 84,
    tempoMedioDias: 32,
    encerramentosPorMes: [
      { mes: 'Jan', quantidade: 6 },
      { mes: 'Fev', quantidade: 9 },
      { mes: 'Mar', quantidade: 7 },
      { mes: 'Abr', quantidade: 8 },
      { mes: 'Mai', quantidade: 5 },
      { mes: 'Jun', quantidade: 4 },
      { mes: 'Jul', quantidade: 6 },
      { mes: 'Ago', quantidade: 7 },
      { mes: 'Set', quantidade: 8 },
      { mes: 'Out', quantidade: 9 },
      { mes: 'Nov', quantidade: 7 },
      { mes: 'Dez', quantidade: 8 },
    ],
  };
}

export function mockRelatorioCumprimentoPrazos(filtros?: MockRelatorioFiltros) {
  const comissoes = filtrarComissoesCriacao(filtros).map((c) => ({
    id: String(c['id']),
    protocolo: String(c['protocolo']),
    titulo: String(c['titulo']),
    prazoConclusao: String(c['prazoConclusao']),
    diasRestantes: diasRestantes(String(c['prazoConclusao'])),
    situacao: situacaoCumprimento(c),
  }));
  const ativas = comissoes.filter((c) => c.situacao !== 'ENCERRADA');
  const noPrazo = ativas.filter((c) => c.situacao === 'NO_PRAZO').length;
  const atencao = ativas.filter((c) => c.situacao === 'ATENCAO').length;
  const atrasadas = ativas.filter((c) => c.situacao === 'ATRASADO').length;
  const taxa =
    ativas.length > 0 ? Math.round(((noPrazo + atencao) / ativas.length) * 100) : 85;
  return { taxaCumprimento: taxa, noPrazo, atencao, atrasadas, comissoes };
}

export function mockRelatorioProdutividade(filtros?: MockRelatorioFiltros) {
  const encerradas = filtrarEncerradas(filtros);
  let tempoMedioDias = 32;
  if (encerradas.length > 0) {
    const totalDias = encerradas.reduce((acc, c) => {
      const ini = new Date(String(c['criadoEm']));
      const fim = new Date(String(c['atualizadoEm']));
      return acc + Math.max(1, Math.round((fim.getTime() - ini.getTime()) / 86_400_000));
    }, 0);
    tempoMedioDias = Math.round(totalDias / encerradas.length);
  }
  return {
    comissoesFinalizadas: encerradas.length || 84,
    tempoMedioConclusaoDias: tempoMedioDias || 32,
    conclusoesPorPeriodo: [
      { periodo: 'Mai', conclusoes: 5 },
      { periodo: 'Set', conclusoes: 6 },
      { periodo: 'Nov', conclusoes: 7 },
      { periodo: 'Dez', conclusoes: 4 },
      { periodo: 'Jan', conclusoes: 8 },
      { periodo: 'Fev', conclusoes: 9 },
      { periodo: 'Mar', conclusoes: 6 },
      { periodo: 'Abr', conclusoes: 7 },
    ],
  };
}

export function mockRelatorioProdutividadeSecoes(filtros?: MockRelatorioFiltros) {
  const base = filtrarComissoesCriacao(filtros);
  const ativas = base.filter(
    (c) => c['status'] === 'EM_ANDAMENTO' || c['status'] === 'INSTAURADA',
  );
  const encerradas = base.filter((c) => c['status'] === 'ENCERRADA');
  const comissoesPorSecao = [
    { secao: 'PM-1', quantidade: 24, tempoMedioRespostaDias: 18 },
    { secao: 'PM-3', quantidade: 21, tempoMedioRespostaDias: 22 },
    { secao: 'PM-5', quantidade: 19, tempoMedioRespostaDias: 25 },
    { secao: 'PM-4', quantidade: 18, tempoMedioRespostaDias: 27 },
    { secao: 'PM-6', quantidade: 12, tempoMedioRespostaDias: 31 },
    { secao: 'PM-2', quantidade: 9, tempoMedioRespostaDias: 29 },
  ];
  const totalPorSecao = comissoesPorSecao.reduce((acc, s) => acc + s.quantidade, 0);
  return {
    totalComissoes: totalPorSecao,
    comissoesAtivas: ativas.length,
    comissoesEncerradas: encerradas.length,
    tempoMedioConclusaoDias: 32,
    taxaRespostaEncaminhamentos: 88,
    comissoesPorSecao,
  };
}

export function mockRelatoriosIndicadores(filtros?: MockRelatorioFiltros) {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const base = filtrarComissoesCriacao(filtros);
  const ativas = base.filter(
    (c) => c['status'] === 'EM_ANDAMENTO' || c['status'] === 'INSTAURADA',
  );
  const encerradas = filtrarEncerradas(filtros);
  const encerradasNoPrazo = encerradas.filter((c) => {
    const prazo = new Date(String(c['prazoConclusao']) + 'T12:00:00');
    const fim = new Date(String(c['atualizadoEm']));
    return fim.getTime() <= prazo.getTime();
  });
  const taxaCumprimento =
    encerradas.length > 0
      ? Math.round((encerradasNoPrazo.length / encerradas.length) * 100)
      : 85;
  return {
    comissoesEmAndamento: ativas.length,
    tempoMedioConclusaoDias: 28,
    taxaCumprimentoPrazos: taxaCumprimento,
    relatorios: [
      {
        id: 'visao-geral',
        titulo: 'Visão Geral das Comissões',
        descricao: 'Panorama consolidado de status, prazos e seções proponentes.',
      },
      {
        id: 'produtividade',
        titulo: 'Relatório de Produtividade',
        descricao: 'Comissões finalizadas, tempo médio e conclusões por período.',
      },
      {
        id: 'produtividade-secoes',
        titulo: 'Produtividade por Seção do EM',
        descricao: 'Volume de comissões e desempenho por seção do Estado-Maior (PM-1 a PM-6).',
      },
      {
        id: 'prazos',
        titulo: 'Relatório de Prazos',
        descricao: 'Situação de prazos por tipo de comissão e alertas de atraso.',
      },
      {
        id: 'encerradas-trimestre',
        titulo: 'Comissões Encerradas no Último Trimestre',
        descricao: 'Relatório de encerramentos formais e vínculos com demandas MG3P.',
      },
      {
        id: 'desempenho-anual',
        titulo: 'Relatório de Desempenho Anual',
        descricao: 'Indicadores agregados por exercício institucional.',
      },
      {
        id: 'cumprimento-prazos',
        titulo: 'Cumprimento de Prazos por Comissão',
        descricao: 'Detalhamento de aderência ao prazo de conclusão por protocolo.',
      },
    ],
  };
}

export function mockRelatorioBySlug(slug: string, filtros?: MockRelatorioFiltros): unknown | null {
  switch (slug) {
    case 'painel':
      return mockRelatoriosPainel(filtros);
    case 'produtividade':
      return mockRelatorioProdutividade(filtros);
    case 'produtividade-secoes':
      return mockRelatorioProdutividadeSecoes(filtros);
    case 'prazos':
      return mockRelatorioPrazos(filtros);
    case 'visao-geral':
      return mockRelatorioVisaoGeral(filtros);
    case 'encerradas-trimestre':
      return mockRelatorioEncerradasTrimestre(filtros);
    case 'desempenho-anual':
      return mockRelatorioDesempenhoAnual(filtros);
    case 'cumprimento-prazos':
      return mockRelatorioCumprimentoPrazos(filtros);
    default:
      return null;
  }
}
